import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { FormDefinitionCacheService, LibFormManagerService } from '@tuain/ng-forms';
import { StorageService } from '@services/storage.service';
import { AppConfigService } from '@services/app-configuration.service';
import { APIGatewayService } from '@services/api-gateway.service';
import { EventManagerService } from '@services/event-manager.service';
import { AppLoggerService } from '@services/app-logger.service';
import { UserSessionService } from '@services/user-session.service';
import { ComponentPageService } from '@services/component-pages.service';
import { ServerEntryPoint } from '@core/utils/url.utils';
import { defaultConfig } from 'src/app/app.config';

const {
  appStarted,
  configLoaded,
  notAuthorized,
  restartApp,
  notifyFormAfterSessionEnd,
  notifyFormAfterSessionStart,
  serverProcessEnded,
  serverProcessStarted,
  sessionEncryptKeyReceived,
  sessionEnded,
  sessionEstablished,
} = defaultConfig.appEvents;

const UNAUTHORIZED = 'UNAUTHORIZED';
// Marcador normalizado de sesión cerrada/expirada (estampado por el gateway en api-response.js).
const SESSION_CLOSED_ERROR_TYPE = 'SESSION_CLOSED';

const SESSIONLESS_HOME = 'SESSIONLESS_HOME';

@Injectable({
  providedIn: 'root',
})
export class FormOperationService extends LibFormManagerService {
  private _apiGateway = inject(APIGatewayService);
  private _router = inject(Router);
  private _eventManager = inject(EventManagerService);
  private _appConfig = inject(AppConfigService);
  private _storageService = inject(StorageService);
  private _componentPageService = inject(ComponentPageService);
  private _userSession = inject(UserSessionService);

  encryptKey: string;
  lastRequestedForm: [string, string, string];
  logoutPath = '';
  formAfterSessionStart = '';
  nextForm: string = defaultConfig?.initialForm;
  serverEntryPoint: ServerEntryPoint = defaultConfig?.serverEntryPoint;

  private _logger = inject(AppLoggerService);
  private _definitionCache = inject(FormDefinitionCacheService);

  constructor() {
    super();
    this.subscribeEvents();
  }

  override async loadStack() {
    this.pageStack = (await this._storageService.getItem('forms', 'pageStack')) ?? [];
  }

  override saveStack() {
    const stackMap = new Map();
    for (let i = this.pageStack.length - 1; i >= 0; i--) {
      const stackItem = this.pageStack[i];
      if (!stackMap.has(stackItem.name)) {
        stackMap.set(stackItem.name, stackItem);
      }
    }
    this.pageStack = Array.from(stackMap.values()).reverse();
    this._storageService.setItem('forms', 'pageStack', this.pageStack);
  }

  extractConfigData() {
    this.logoutPath = this._appConfig.getParameter('logoutPath');
    this.serverEntryPoint = this._appConfig.getParameter('serverEntryPoint');
  }

  subscribeEvents() {
    this._eventManager.subscribe(configLoaded.name, data => {
      data && this.extractConfigData();
    });
    this._eventManager.subscribe(appStarted.name, () => this.loadStack());
    this._eventManager.subscribe(restartApp.name, () => this.goToForm('citizenLogin'));
    this._eventManager.subscribe(notAuthorized.name, () => this.goToForm(UNAUTHORIZED));
    this._eventManager.subscribe(sessionEncryptKeyReceived.name, data => this.updateEncryptKey(data));
    // Establecimiento de sesión
    this._eventManager.subscribe(sessionEstablished.name, data => data && this.newSessionEstablished(data));
    // Eventos no usados en backoffice (revisar necesidad desde agente)
    this._eventManager.subscribe(notifyFormAfterSessionStart.name, form => {
      form && typeof form === 'string' && (this.formAfterSessionStart = form);
    });
    this._eventManager.subscribe(notifyFormAfterSessionEnd.name, form => {
      form && typeof form === 'string' && (this.nextForm = form);
    });
    // Cierre de sesión
    this._eventManager.subscribe(sessionEnded.name, data => {
      this._logger.debug('[FormManager] Session ended event received');
      // El cierre de la sesión de ecosistema en el gateway ahora lo hace AuthService.logout()
      // de forma awaited (antes del redirect a Hydra). Antes aquí se llamaba closeSession()
      // fire-and-forget y el redirect a Hydra lo abortaba, dejando la sesión viva en el gateway.
      // Reseteamos las funciones para que ninguna definición cacheada se re-renderice con permisos
      // de la sesión anterior si el usuario alcanza a ver un formulario antes de navegar a la home.
      this._definitionCache.setUserFunctions([]);
      if (SESSIONLESS_HOME) {
        this.goToForm(SESSIONLESS_HOME);
      }
      if (data?.formToOpen) {
        const nextFormAfterClose = data?.formToOpen ?? this.nextForm;
        if (nextFormAfterClose) {
          this.goToForm(nextFormAfterClose);
        }
      }
    });
  }

  newSessionEstablished(fullSessionData) {
    const { sessionData, privileges, changePage } = fullSessionData ?? {};
    privileges && this._definitionCache.setUserFunctions(privileges);
    if (!sessionData) {
      return;
    }
    let [nextForm, token, subject]: [string, string, string] = [null, null, null];
    if (changePage) {
      if (this.formAfterSessionStart) {
        nextForm = this.formAfterSessionStart ?? 'mainDashboard';
      } else {
        [nextForm, token, subject] = this.lastRequestedForm ?? [];
      }
      this.goToForm(nextForm ?? 'mainDashboard', token, subject);
    }
  }

  updateEncryptKey(encryptKey) {
    this.encryptKey = encryptKey;
  }

  /**
   * Definición del formulario, ya recortada según las funciones del usuario.
   *
   * El protocolo completo —pedir las firmas del catálogo, invalidar lo que cambió, cachear y recortar—
   * lo aporta `FormDefinitionCacheService` desde `@tuain/ng-forms` 22.4. Las definiciones locales de
   * respaldo se registran en `main.ts` con `defaultDefinitions`.
   */
  override getFormDefinition(code: string) {
    return this._definitionCache.getDefinition(code);
  }

  override async execServerAction(actionRequest) {
    try {
      this._eventManager.next(serverProcessStarted.name, null);
      const formActionResponse = await this._apiGateway.executeFormAction(actionRequest);
      this._eventManager.next(serverProcessEnded.name, null);
      const { error } = formActionResponse ?? {};
      // Sesión cerrada/expirada: el cierre ya lo disparó APIGatewayService.detectSessionClosed
      // (modal → sessionEnded). No devolvemos la respuesta para no mostrar además un error de
      // formulario redundante. Cubre el antiguo caso forms-lib-formActionExec-sessionNotFound/1004.
      if (error?.errorType === SESSION_CLOSED_ERROR_TYPE) {
        return null;
      }
      if (formActionResponse && formActionResponse.data) {
        return formActionResponse;
      }
    } catch (e) {
      this._logger.error(`Excepción solicitando acción ${actionRequest}: ${e}`);
    }
    return null;
  }

  override goToForm(formName, token = null, subject = null) {
    this._logger.debug('goToForm', formName);
    this.lastRequestedForm = [formName, token, subject];
    const formRoute = this._componentPageService.formRoute(formName);
    const navigationArray = [formRoute];
    (token || subject) && navigationArray.push(token ?? 0);
    subject && navigationArray.push(subject);

    this._router.navigate(navigationArray);
  }
}
